import { graphHooks } from '@tunasong/graph-lib/react'
import { logger, walkParentChain } from '@tunasong/models'
import type { Entity, Persisted } from '@tunasong/schemas'
import { NO_PARENT, isBandSpace, isFolder } from '@tunasong/schemas'
import { useEffect, useState } from 'react'

export interface BandRef {
  id: string
  name: string
}

export type SongBand = { status: 'resolving' } | { status: 'none' } | { status: 'band'; band: BandRef }

/** Principals that make an entity public, not shared with a person. */
const publicPrincipals = new Set(['PUBLIC', 'AUTHENTICATED'])

const isTopLevel = (entity: { parentId?: string | null }) => !entity.parentId || entity.parentId === NO_PARENT

const isSpace = (entity: unknown) => isFolder(entity) && (entity.tags ?? []).includes('space')

/** Shared with at least one other person: an ACL principal that is neither the owner nor public. */
const isSharedWithOthers = (entity: { userId?: string; acls?: { principal: string }[] }) =>
  (entity.acls ?? []).some(acl => acl.principal !== entity.userId && !publicPrincipals.has(acl.principal))

const toBandRef = (entity: { id?: string; name?: string }): BandRef | undefined =>
  entity.id ? { id: entity.id, name: entity.name ?? 'your band' } : undefined

/**
 * The band in a song's ancestor chain (nearest first). A band is:
 *  1. the nearest bandspace, or
 *  2. failing that, the song's TOP-LEVEL Space (a folder tagged `space`) when it is shared with
 *     someone other than its owner. A personal Space is not a band, and a sub-Space never counts as a
 *     band of its own.
 *
 * A chain cut short by an unreadable ancestor has no known top level, so it yields no Space band.
 */
export const findBand = (chain: readonly Persisted<Entity>[]): BandRef | undefined => {
  const bandspace = chain.find(ancestor => isBandSpace(ancestor))
  if (bandspace) {
    return toBandRef(bandspace)
  }
  const top = chain.at(-1)
  if (top && isTopLevel(top) && isSpace(top) && isSharedWithOthers(top)) {
    return toBandRef(top)
  }
  return undefined
}

/**
 * The band a song belongs to (see `findBand`). Inside a setlist this is the SONG's band, not the
 * setlist's.
 *
 * Not `graphHooks.useAncestors`: that hook keeps the previous entity's ancestors while a new walk is
 * in flight, and keeps them for good when the new entity has no parent, which would show the previous
 * song's band part. The result here is keyed on the entity id, so a stale walk reads as `resolving`.
 *
 * An ancestor the user cannot read (a song shared directly, or a band they were removed from) ends
 * the walk, and the song is treated as personal rather than left resolving forever.
 */
export const useSongBand = (entity?: Persisted<Entity>): SongBand => {
  const [loadEntity] = graphHooks.useLoadEntity()
  const [walk, setWalk] = useState<{ forId: string; result: SongBand } | null>(null)
  const entityId = entity?.id
  const parentId = entity?.parentId

  useEffect(() => {
    if (!entityId || !parentId) {
      return
    }
    let cancelled = false
    walkParentChain<Persisted<Entity>>({
      startId: parentId,
      load: id =>
        loadEntity({ id }, true)
          .unwrap()
          .catch(() => undefined),
    })
      .then(({ chain }) => {
        if (cancelled) {
          return
        }
        const band = findBand(chain)
        setWalk({ forId: entityId, result: band ? { status: 'band', band } : { status: 'none' } })
      })
      .catch(error => logger.warn('useSongBand: could not walk the parent chain', { entityId, error }))
    return () => {
      cancelled = true
    }
  }, [entityId, parentId, loadEntity])

  if (!entityId || !parentId) {
    return { status: 'none' }
  }
  return walk?.forId === entityId ? walk.result : { status: 'resolving' }
}
